/**
 * TransferPicker — uitklap-paneel in de softphone (patroon DTMFKeypad) om een
 * actief gesprek door te verbinden: collega's met live presence-badge of een
 * extern nummer, direct (blind) of met overleg vooraf (warm).
 *
 * Transfer is een pure REST-call (geen SDK-actie), dus elke tab mag hem
 * direct doen; de gespreksstaat komt terug via de Realtime-subscription op
 * `calls` (zie docs/telefoon-multitab-architectuur.md).
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import {
  useColleaguePresence,
  type Colleague,
  type ColleagueAvailability,
} from '../../hooks/useColleaguePresence';

const BADGE: Record<ColleagueAvailability, { label: string; className: string }> = {
  'beschikbaar': { label: 'Beschikbaar', className: 'bg-emerald-100 text-emerald-700' },
  'in-gesprek': { label: 'In gesprek', className: 'bg-amber-100 text-amber-700' },
  'niet-storen': { label: 'Niet storen', className: 'bg-slate-200 text-slate-500' },
  'geen-registratie': { label: 'Geen registratie', className: 'bg-slate-100 text-slate-400' },
};

type TransferMode = 'blind' | 'warm';

interface Props {
  callId: string;
  onClose: () => void;
  /** Warm doorverbinden pas tonen als die fase live is (belproef-gate). */
  warmEnabled?: boolean;
}

async function postTransfer(body: Record<string, unknown>): Promise<{ ok: boolean; error?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return { ok: false, error: 'Niet ingelogd' };
  const r = await fetch('/api/telephony/transfer', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ error: 'Doorverbinden mislukt' }));
    return { ok: false, error: (err as { error?: string }).error ?? 'Doorverbinden mislukt' };
  }
  return { ok: true };
}

export function TransferPicker({ callId, onClose, warmEnabled = true }: Props) {
  const [ownUserId, setOwnUserId] = useState<string | null>(null);
  useEffect(() => {
    void supabase.auth.getSession().then(({ data: { session } }) => {
      setOwnUserId(session?.user?.id ?? null);
    });
  }, []);

  const { colleagues, isLoading, error: listError } = useColleaguePresence({
    excludeUserId: ownUserId,
  });

  const [mode, setMode] = useState<TransferMode>('blind');
  const [externalNumber, setExternalNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doTransfer = async (target: { type: 'colleague'; userId: string } | { type: 'number'; number: string }) => {
    setBusy(true);
    setError(null);
    const result = await postTransfer({ callId, target, mode });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Doorverbinden mislukt');
      return;
    }
    // Blind: de eigen leg valt zo vanzelf weg (normale einde-flow).
    // Warm: de consult-staat verschijnt via Realtime in ActiveView.
    onClose();
  };

  const pickColleague = (c: Colleague) => {
    if (!c.selectable || busy) return;
    void doTransfer({ type: 'colleague', userId: c.userId });
  };

  const submitExternal = () => {
    const number = externalNumber.trim();
    if (!number || busy) return;
    void doTransfer({ type: 'number', number });
  };

  return (
    <div
      role="dialog"
      aria-label="Doorverbinden"
      className="bg-white border-t border-gray-200 p-3 space-y-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">Doorverbinden</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Doorverbinden sluiten"
          className="text-xs text-gray-500 hover:text-gray-800 px-2 py-0.5 rounded hover:bg-gray-100"
        >
          Sluiten (Esc)
        </button>
      </div>

      {warmEnabled && (
        <div className="flex gap-1.5" role="radiogroup" aria-label="Manier van doorverbinden">
          {([
            { key: 'blind', label: 'Direct' },
            { key: 'warm', label: 'Eerst overleggen' },
          ] as Array<{ key: TransferMode; label: string }>).map((m) => (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={mode === m.key}
              onClick={() => setMode(m.key)}
              className={clsx(
                'px-2.5 py-1 rounded text-xs font-medium',
                mode === m.key
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
              )}
            >
              {m.label}
            </button>
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-500">
        {mode === 'blind'
          ? 'Je verlaat het gesprek zodra je doorverbindt.'
          : 'De klant hoort wachtmuziek terwijl jij eerst met je collega overlegt.'}
      </p>

      {(error || listError) && (
        <div role="status" className="px-2 py-1.5 bg-red-50 border border-red-200 text-red-800 rounded text-xs">
          {error ?? listError}
        </div>
      )}

      {isLoading ? (
        <div className="h-12 bg-gray-50 rounded animate-pulse" />
      ) : colleagues.length === 0 ? (
        <p className="text-xs text-gray-400">Geen collega's met een belregistratie gevonden.</p>
      ) : (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {colleagues.map((c) => {
            const badge = BADGE[c.availability];
            return (
              <li key={c.userId}>
                <button
                  type="button"
                  onClick={() => pickColleague(c)}
                  disabled={!c.selectable || busy}
                  className={clsx(
                    'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left text-sm',
                    c.selectable && !busy
                      ? 'bg-gray-50 hover:bg-indigo-50 text-gray-800'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed opacity-70',
                  )}
                >
                  <span className="truncate">{c.name}</span>
                  <span className={clsx('text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0', badge.className)}>
                    {badge.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex gap-2 pt-1 border-t border-gray-100">
        <input
          type="tel"
          placeholder="Extern nummer (06… of +31…)"
          value={externalNumber}
          onChange={(e) => setExternalNumber(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitExternal();
          }}
          className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-sm min-w-0"
        />
        <button
          type="button"
          onClick={submitExternal}
          disabled={!externalNumber.trim() || busy}
          className={clsx(
            'px-3 py-1.5 rounded text-sm font-medium shrink-0',
            !externalNumber.trim() || busy
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-indigo-600 text-white hover:bg-indigo-700',
          )}
        >
          {busy ? 'Bezig…' : 'Verbind door'}
        </button>
      </div>
    </div>
  );
}
